import {Component, OnDestroy, OnInit, ViewChild}        from '@angular/core';
import {FormBuilder, FormGroup, Validators}             from "@angular/forms";
import {NgbActiveModal}                                 from "@ng-bootstrap/ng-bootstrap";
import {HttpErrorResponse}                              from "@angular/common/http";

import {AngularQuery}                                   from "../../services/query/angularquery";
import {ItemComponent}                                  from "../../articles/item/item.component";
import {Message}                                        from "../../services/message/message";
import {MessageEntry}                                   from "../../services/message/message-entry";
import {League}                                         from "../../services/team/league";
import {Sport}                                          from "../../services/sport/sport";
import {SportService}                                   from "../../services/sport/sport.service";
import {Team}                                           from "../../services/team/team";
import {TeamService}                                    from "../../services/team/team.service";
import {Vendor}                                         from "../../services/vendor/vendor";
import {VendorLink}                                     from "../../services/vendor/vendor-link";
import {VendorService}                                  from "../../services/vendor/vendor.service";

import {isNullOrUndefined}                              from "util";
import {Subscription}                                   from "rxjs/Subscription";



@Component({
	selector: 'app-team-admin',
	templateUrl: './team-admin.component.html',
	styleUrls: ['./team-admin.component.css']
})


/**
 * Displays and manages the Team Administration screen.
 *
 */
export class TeamAdminComponent implements OnInit, OnDestroy {


	conference: string[] = ['None', 'AFC', 'NFC'];
	division: string[] = [];
	divisionFound: Subscription;
	hideSearch: boolean = false; // Display the 'Edit an existing team' choice on startup.
	league: League[] = [];
	leagueFound: Subscription;
	editIndex: number;
	editLink: VendorLink;
	editVendorValue: boolean = false;
	message:Message;
	sport: Sport[] = [];
	sportFound: Subscription;
	team: Team;
	teamFound: Subscription;
	tForm: FormGroup;
	vendor: VendorLink[] = [];
	vendorFound: Subscription;
	vendorList: Vendor[] = [];
	vendorListFound: Subscription;



	/**
	 * Initialize the object.
	 *
	 * @param activeModal      Used to make this a modal Component.
	 * @param fb               The FormBuilder for the page.
	 * @param sportService     The service that provides Sport information.
	 * @param teamService      The service that provides Team information.
	 * @param vendorService    The VendorService that retrieves vendor (VendorLink) data.
	 */
	constructor(
		public activeModal: NgbActiveModal,
        private fb: FormBuilder,
        private sportService: SportService,
        private teamService: TeamService,
        private vendorService: VendorService,
    ) {}


	/**
	 * Clear any subscriptions created earlier.
	 */
	ngOnDestroy() {

		if (this.divisionFound) {
			this.divisionFound.unsubscribe();
		}

		if (this.leagueFound) {
			this.leagueFound.unsubscribe();
		}

		if (this.sportFound) {
			this.sportFound.unsubscribe();
		}

		if (this.teamFound) {
			this.teamFound.unsubscribe();
		}

		if (this.vendorFound) {
			this.vendorFound.unsubscribe();
		}

		if (this.vendorListFound) {
			this.vendorListFound.unsubscribe();
		}

	}


	/**
	 * Initialize the form, then arrange for the information to fill it.
	 */
	ngOnInit() {

		// Initialize the data structure for the page.
		this.tForm = this.fb.group({
			title: ['', [Validators.required,Validators.minLength(1), Validators.maxLength(64),]],
			shorttitle: ['', [Validators.required,Validators.minLength(1), Validators.maxLength(16),]],
			id: ['', [Validators.required, Validators.min(-1)]],
			code: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(3)]],
			teamcode: ['', [Validators.required, Validators.min(1), Validators.maxLength(4)]],
			conference: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(4)]],
			division: ['', [Validators.maxLength(10)]],
			leagueid: ['', [Validators.required, Validators.min(0)]],
			vendor_value: ['', []],
			create_edit: ['existing', []],
		});

		// Set up the message window with an initial message.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Loading...';
		me.level = 1;
		this.message = new Message(me);
		this.message.show();

		this.tForm.controls['code'].setValue('NFL');

		// Get the list of vendors.
		this.vendorListFound = this.vendorService.getVendors().subscribe(

			(vendors) => {

				if (Array.isArray(vendors)) {

					// Fill it with the new ones.
					for (let vendor of vendors) {

						this.vendorList.push(new Vendor(vendor));

					}

					me.message = 'Ready';
					me.level = 2;
					this.message.add(me);
					this.message.show();

				} else {

					me.message = 'Existing vendor data did not load properly';
					me.level = 4;
					this.message.add(me, true);
					this.message.show();

				}

			},

			() => {

				me.message = 'Vendor data did not load properly';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

		// Start the retrieval of data; return the Observer and process when data arrives.
		this.sportFound = this.sportService.getSports().subscribe(

			(response) => {

				// Pass back the array of Sport objects.
				this.sport = response;
				this.onSportChange('NFL');
				// Get the team data now.
				this.teamFound = this.teamService.getTeamById(-1).subscribe(

					(data) => {

						this.team = new Team(data);
						this.onTeamChange(this.team);

					},

					() => {

						me.message = 'Existing team data did not load properly';
						me.level = 4;
						this.message.add(me, true);
						this.message.show();

					}

				);

			},

			() => {

				me.message = 'Sport data did not load properly';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * Lets me talk to the Team typeahead.
	 */
	@ViewChild('teamTypeahead')
	teamTypeahead: any;


	/**
	 * Lets me talk to the Vendor input.
	 */
	@ViewChild('vendorInput')
	vendorInput: any;


	/**
	 * Lets me talk to the Vendor Edit input.
	 */
	@ViewChild('vendorEditInput')
	vendorEditInput: any;


	/**
	 * Lets me talk to the Vendor no choice box.
	 */
	@ViewChild('vendorNoChoice')
	vendorNoChoice: any;


	/**
	 * Lets me talk to the Vendor select.
	 */
	@ViewChild('vendorSelect')
	vendorSelect: any;


	/**
	 * Add the current vendor entry to this player.
	 * This is a new entry; there are separate routines
	 * to handle editing existing entries.
	 */
	addVendorEntry():void {

		if (0 === this.vendorInput.nativeElement.value.trim().length) {
			let me:MessageEntry = new MessageEntry();
			me.message = 'Blank identifiers are not allowed in vendor entries';
			me.level = 4;
			this.message.add(me, true);
			this.message.show();
			return;
		}

		let entry:VendorLink = new VendorLink();
		entry.id = -1;
		entry.origin_id = -1;
		entry.vendor_link = this.vendorInput.nativeElement.value;
		entry.vendor_id = this.vendorSelect.nativeElement.value;
		entry.name = this.vendorSelect.nativeElement.selectedOptions[0].label;

		if (this.checkVendor(entry.vendor_id)) {
			let me:MessageEntry = new MessageEntry();
			me.message = 'Please edit the existing \'' + entry.name + '\' entry instead of making a new one.';
			me.level = 4;
			this.message.add(me, true);
			this.message.show();
			return;
		}

		this.vendor.push(entry);
		this.vendorInput.nativeElement.value = '';

		let me:MessageEntry = new MessageEntry();
		me.message = 'Added \'' + entry.name + '\' entry';
		me.level = 1;
		this.message.add(me, true);
		this.message.show();

	}


	/**
	 * Cancel an existing edit of a vendor entry.
	 */
	cancelVendorEdit():void {

		this.editVendorValue = false;

	}


	/**
	 * Check the existing vendor list to see if that vendor
	 * already has an entry for this team.
	 *
	 * @param  {number} id  The vendor ID to search for.
	 * @return {boolean} True if an entry exists, false otherwise.
	 */
	checkVendor(id:number):boolean {

		let existingItem:VendorLink = this.vendor.find(obj => obj.vendor_id === id);
		return ! isNullOrUndefined(existingItem);

	}


	/**
	 * Clear the fields to get ready for another entry.
	 */
	clear(): void {

		this.tForm.controls['title'].setValue('');
		this.tForm.controls['shorttitle'].setValue('');
		this.tForm.controls['id'].setValue(-1);
		this.tForm.controls['teamcode'].setValue('');
		this.tForm.controls['conference'].setValue('None');
		this.tForm.controls['division'].setValue('');
		this.tForm.controls['leagueid'].setValue(0);
		this.teamTypeahead.typeaheadTeamInput.nativeElement.value = '';
		this.vendor.length = 0;
		this.vendorInput.nativeElement.value = '';
		this.vendorEditInput.nativeElement.value = '';

	}


	/**
	 * Delete the specified vendor entry from the list.  This is the index of
	 * the entry in the current list, not the ID value of the entry.
	 *
	 * @param {number} index  The entry to remove from the list of vendor entries.
	 */
	deleteVendorEntry(index:number): void {

		if (this.editVendorValue) {

			window.alert('Finish or cancel your existing edit.');
			return;

		}

		this.vendor.splice(index, 1);

	}


	/**
	 * Edit the specified vendor entry.  This is the index of
	 * the entry in the current list, not the ID value of the entry.
	 *
	 * @param {VendorLink} entry  The entry to edit in the list of vendor entries.
	 * @param {number} index  Index of the entry in the current list.
	 */
	editVendorEntry(entry:VendorLink, index:number): void {

		if (this.editVendorValue) {

			window.alert('Finish or cancel your existing edit.');
			return;

		}

		this.editIndex = index;
		this.editLink = entry;
		this.vendorNoChoice.nativeElement.value = this.editLink.name;
		this.vendorEditInput.nativeElement.value = this.editLink.vendor_link;
		this.editVendorValue = true;

	}


	/**
	 * This is used by the Create/Edit radio button to
	 * turn the selected value a different color.
	 *
	 * @param name  The value for the radio button, either 'new' or 'existing'.
	 * @returns {any} A style object with content or empty.
	 */
	isCreateEditHighlighted(name: string): any {

		if (name === this.tForm.controls['create_edit'].value) {

			return {'color': 'blue', 'font-weight': 'bold'};

		}

		return {};

	}


	/**
	 * This is used by the Sport radio button to
	 * turn the selected value a different color.
	 *
	 * @param name  The 'code' value for the radio button.
	 * @returns {any} A style object with content or empty.
	 */
	isHighlighted(name: string): any {

		if (name === this.tForm.controls['code'].value) {

			return {'color': 'blue', 'font-weight': 'bold'};

		}

		return {};

	}


	/**
	 * Change the League pulldown and VendorLink data when required.
	 *
	 * @param code  The new 'code' value to use when retrieving positions.
	 */
	onSportChange(code: string):void {

		// Start the retrieval of data; return the Observer and process when data arrives.
		this.league.length = 0;
		let leaguenone:League = new League();
		leaguenone.id = 0;
		leaguenone.code = code;
		leaguenone.title = 'None';
		this.league.push(leaguenone);
		this.tForm.controls['leagueid'].setValue(0);

		if ('MLB' === code) {

			this.leagueFound = this.teamService.getLeague(code).subscribe(

				(response) => {

					if (Array.isArray(response)) {

						// Fill it with the new ones.
						for (let league of response) {

							this.league.push(new League(league));

						}

						this.tForm.controls['leagueid'].setValue(0);

						// Notify the user.
						let me:MessageEntry = new MessageEntry();
						me.message = 'Ready';
						me.level = 2;
						this.message.add(me);
						this.message.show();

					} else {

						// Notify the user.
						let me:MessageEntry = new MessageEntry();
						me.message = 'MLB league information was incorrectly formatted';
						me.level = 4;
						this.message.add(me, true);
						this.message.show();

					}

				},

				() => {

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'MLB league information did not load properly';
					me.level = 4;
					this.message.add(me, true);
					this.message.show();

				}

			);

		}

		// Get the new division choices.
		this.division.length = 0;
		this.division.push('');
		this.tForm.controls['division'].setValue('');

		if ('MLB' === code || 'NFL' === code) {

			this.divisionFound = this.teamService.getDivision(code).subscribe(

				(division) => {

					if (Array.isArray(division)) {

						// Fill it with the new ones.
						for (let entry of division) {

							this.division.push(entry['division']);

						}

						this.tForm.controls['division'].setValue('');

						// Notify the user.
						let me:MessageEntry = new MessageEntry();
						me.message = 'Ready';
						me.level = 2;
						this.message.add(me);
						this.message.show();

					} else {

						// Notify the user.
						let me:MessageEntry = new MessageEntry();
						me.message = 'Division information was incorrectly formatted';
						me.level = 4;
						this.message.add(me, true);
						this.message.show();

					}

				},

				() => {

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'Division information did not load properly';
					me.level = 4;
					this.message.add(me, true);
					this.message.show();

				}

			);

		}

	}


	/**
	 * Handle the 'Save' or 'Save and Close' button click.
	 *
	 * @param {Object}  data  The information submitted with the page.
	 * @param {boolean} close Whether to close the modal upon success.
	 */
	onSubmit(data:object, close:boolean) {

		if ( true === data['valid'] ) {

			let title:string = data['value']['title'];

			// Notify the user.
			let me:MessageEntry = new MessageEntry();
			me.message = "Saving '" + title + "' ...";
			me.level = 1;
			this.message.add(me, true);
			this.message.show();

			// Gather the data for output, removing the things that aren't needed.
			let json:object = {};
			json['title'] = ItemComponent.replaceWordCharacters(data['value']['title']);
			json['shorttitle'] = ItemComponent.replaceWordCharacters(data['value']['shorttitle']);
			json['code'] = data['value']['code'];
			json['teamcode'] = data['value']['teamcode'];
			json['conference'] = data['value']['conference'];
			json['division'] = data['value']['division'];
			json['leagueid'] = data['value']['leagueid'];
			json['vendors'] = this.vendor;

			// Build the query.
			let q:AngularQuery = new AngularQuery();
			q.useCacheBuster( false );
			q.useParams( false );
			let isNewTeam:boolean = false;

			if ( -1 === data['value']['id'] ) {

				q.setEndpoint( AngularQuery.ENDPOINT_TEAM, 'new' );
				isNewTeam = true;

			} else {

				q.setEndpoint( AngularQuery.ENDPOINT_TEAM, data['value']['id'].toString() );

			}

			// Unsubscribe from any existing player query.
			if (this.teamFound) {

				this.teamFound.unsubscribe();

			}

			// Start the retrieval of data; return the Observer and process when data arrives.
			this.teamFound = this.teamService.save( q.getUrl(), JSON.stringify( json ) ).subscribe(

				(response) => {

					// Close the window if the Save & Close button was clicked.
					if ( close ) {

						this.activeModal.close('Save and Close pressed');

					} else {

						this.team = new Team(response);
						this.tForm.patchValue(this.team);

						// Switch this from a 'create team' view to 'edit team' view.
						if (isNewTeam) {

							this.teamTypeahead.typeaheadTeamInput.nativeElement.value = this.team.title;
							this.hideSearch = true;
							this.tForm.controls['create_edit'].setValue('existing');

						}

						// Notify the user.
						me.message = "Saved '" + title + "'";
						me.level = 2;
						this.message.add(me);
						this.message.show();

					}

				},

				() => {

					// Notify the user.
					me.message = "Unable to save '" + title + "'";
					me.level = 4;
					this.message.add(me);
					this.message.show();

				}

			);

		}

	}


	/**
	 * When the user selects a Team, set the values
	 * as needed.
	 *
	 * @param event  The event that comes from the Team typeahead.
	 */
	onTeamChange(event: any): void {

		let team: Team = new Team(event);

		this.tForm.controls['title'].setValue(team.title);
		this.tForm.controls['shorttitle'].setValue(team.shorttitle);
		this.tForm.controls['id'].setValue(team.id);
		this.tForm.controls['code'].setValue(team.code);
		this.tForm.controls['teamcode'].setValue(team.teamcode);
		this.tForm.controls['conference'].setValue(team.conference);
		this.tForm.controls['division'].setValue(team.division);
		this.tForm.controls['leagueid'].setValue(team.leagueid);
		this.hideSearch = (-1 !== team.id);
		this.tForm.controls['create_edit'].setValue('existing');

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Retrieving vendor information for ' + team.title;
		me.level = 2;
		this.message.add(me);
		this.message.show();

		// This uses a reference to the Team typeahead to set the text box to the selected team title.
		this.teamTypeahead.typeaheadTeamInput.nativeElement.value = team.title;

		// Clear the existing vendor information array.
		this.vendor.length = 0;

		// Start the retrieval of vendor data; return the Observer and process when data arrives.
		this.vendorFound = this.vendorService.getTeamVendorData(team.id).subscribe(

			(response) => {

				// Having no vendor data is allowed.
				if (response instanceof HttpErrorResponse && response.status === 404) {

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'Ready';
					me.level = 2;
					this.message.add(me);

				} else if (response instanceof HttpErrorResponse && response.status >= 400) {

					// Notify the user.
					let me: MessageEntry = new MessageEntry();
					me.message = 'Vendor information was not found';
					me.level = 4;
					this.message.add(me, true);

				} else if (Array.isArray(response)) {

					// Clear the old elements.
					this.vendor.length = 0;

					// Fill it with the new ones.
					for (let vendor of response) {

						this.vendor.push(new VendorLink(vendor));

					}

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'Ready';
					me.level = 2;
					this.message.add(me);

				} else {

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'Vendor information was incorrectly formatted';
					me.level = 4;
					this.message.add(me, true);

				}

				this.message.show();

			},

			(error) => {

				// Having no vendor data is allowed.
				if (error instanceof HttpErrorResponse && error.status === 404) {

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'Ready';
					me.level = 2;
					this.message.add(me);

				} else {

					// Notify the user.
					let me: MessageEntry = new MessageEntry();
					me.message = 'Vendor information was not found';
					me.level = 4;
					this.message.add(me, true);

				}

				this.message.show();

			}

		);

	}


	/**
	 * Runs when the user has decided to replace the existing
	 * vendor entry with the one they are editing.
	 */
	replaceVendorEntry():void {

		this.editLink.vendor_link = this.vendorEditInput.nativeElement.value;
		this.vendor.splice(this.editIndex, 1, this.editLink);
		this.editVendorValue = false;
		this.vendorEditInput.nativeElement.value = '';

	}


	/**
	 * Toggle between the 'search' and the
	 * 'create new' choices.
	 *
	 * @param {string} name  'new' or 'existing', depending on the type of search.
	 */
	showHideSearch(name:string): void {

		this.tForm.controls['create_edit'].setValue(name);
		this.hideSearch = ('new' === name);
		this.clear();

	}



}



// WEBPACK FOOTER //
// ./src/app/admin/team/team-admin.component.ts